//#region Imports
import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, Platform, SafeAreaView, ScrollView, FlatList, View, ActivityIndicator } from "react-native";
import { useTheme } from "@react-navigation/native";
import { useAccountContext } from "../../hooks/useAccountContext";
import CustomButton from "../../components/CustomButton";
import { useEvents } from "../../hooks/useEvents";
import MyRegistrations from "../SelfService/Events/MyRegistrations";
import RoleGuard from "../../components/RoleGuard";
import { TASKS, TASK_OPTIONS } from "../../constants/Tasks";
// * Language
import { useTranslation } from "react-i18next";
//#endregion

const HomePageEvents = ({ route }) => {
    //#region Const Definitions
    const { t } = useTranslation();
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { account, managerEmployees } = useAccountContext();
    const [isLoading, setIsLoading] = useState(true);
    const buttonTable = [1, 2, 3];

    const { loadUsersRegEvents, loadAllEvents } = useEvents();
    const [showMyRegistrationsScreen, setShowMyRegistrationsScreen] = useState(1);
    const [showFacilitatorsScreen, setShowFacilitatorsScreen] = useState(0);
    const showFacilitatorsScreenRef = useRef(null);
    const showMyRegistrationsScreenRef = useRef(null);

    const [usersRegistrations, setUsersRegistrations] = useState([]);
    const [allEvents, setAllEvents] = useState([]);
    const [refresh, setRefresh] = useState(true);
    //#endregion

    //#region UseEffect
    useEffect(() => {
        setShowMyRegistrationsScreen(1);
        setShowFacilitatorsScreen(0);
        setIsLoading(true);
        loadUsersRegEvents().then((res) => {
            setUsersRegistrations(res);
        }).catch((err) => {
            showMessage({
                message: t("common:loadError"),
                type: "danger",
                duration: 3000,
            });
        })
        loadAllEvents().then((res) => {
            setAllEvents(res);
        }).catch((err) => {
            showMessage({
                message: t("common:loadError"),
                type: "danger",
                duration: 3000,
            });
        })
        setRefresh(false);
        setIsLoading(false);
    }, [refresh]);

    const selectMyRegistrationsScreen = () => {
        setShowMyRegistrationsScreen(1);
        setShowFacilitatorsScreen(0);
    };

    const selectFacilitatorsScreen = () => {
        setShowMyRegistrationsScreen(0);
        setShowFacilitatorsScreen(1);
    };

    useEffect(() => {
        showFacilitatorsScreenRef.current = showFacilitatorsScreen;
    }, [showFacilitatorsScreen]);

    useEffect(() => {
        showMyRegistrationsScreenRef.current = showMyRegistrationsScreen;
    }, [showMyRegistrationsScreen]);

    //* handles refresh in web
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            event.preventDefault();
            try {
                localStorage.setItem(
                    "showMyRegistrationsScreen",
                    showMyRegistrationsScreenRef.current
                );
                localStorage.setItem(
                    "showFacilitatorsScreen",
                    showFacilitatorsScreenRef.current
                );
                localStorage.setItem("isRefresh", "true");
                localStorage.setItem("lastVisitedScreen", "HomePageEvents");
            } catch (error) {
                console.log(error.message);
            }
        };
        if (Platform.OS === "web") {
            window.addEventListener("beforeunload", handleBeforeUnload);
            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }

    }, []);

    //* if refreshed, get data from session storage,
    //* to show same page as before refresh
    const checkIfRefreshed = async () => {
        try {
            let isRefresh = localStorage.getItem("isRefresh");
            if (isRefresh === "true") {
                localStorage.setItem("isRefresh", "false");
                setShowMyRegistrationsScreen(
                    parseInt(localStorage.getItem("showMyRegistrationsScreen"))
                );
                setShowFacilitatorsScreen(
                    parseInt(localStorage.getItem("showFacilitatorsScreen"))
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (Platform.OS === "web") {
            checkIfRefreshed();
        }
    }, []);

    //#endregion

    //#region Loading
    if (isLoading) {
       return (
             <SafeAreaView style={styles.main}>
               <ActivityIndicator animating={true} size='large' color="#86BC25" />
             </SafeAreaView>
           )
    }

    return (
        <SafeAreaView style={styles.main}>
            {/* SubMenu */}
            <View>
                <FlatList
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    directionalLockEnabled={true}
                    alwaysBounceVertical={false}
                    data={buttonTable}
                    contentContainerStyle={{
                        alignSelf: "flex-start",
                        marginBottom: "1%",
                        marginLeft: "2%",
                    }}
                    showsVerticalScrollIndicator={true} //shows horizontal scroll bar on web
                    renderItem={({ index }) => {
                        return (
                            <SafeAreaView>
                                {index == 1 && (
                                    <RoleGuard parentTask={TASKS.ALLEMPLOYEETASKS} task={TASK_OPTIONS.MYREGISTRATIONS} requireCRUD>
                                    <CustomButton
                                            onPress={() => {
                                                selectMyRegistrationsScreen();
                                            }}
                                            text={t("navigate:MR")}
                                            iconName="document-text-outline"
                                            show={showMyRegistrationsScreen ? "HOME_SELECTED" : "HOME"}
                                            type={showMyRegistrationsScreen ? "HOME_SELECTED" : "HOME"}
                                            textTop="0"
                                            sz={Platform.OS !== "web" ? 10 : 25}
                                            isGradient={showMyRegistrationsScreen ? true : false}
                                        />
                                    </RoleGuard>
                                )}

                                {index == 2 && (
                                    <CustomButton
                                            onPress={() => {
                                                selectFacilitatorsScreen();
                                            }}
                                            text={t("navigate:EF")}
                                            iconName="people-outline"
                                            show={showFacilitatorsScreen ? "HOME_SELECTED" : "HOME"}
                                            type={showFacilitatorsScreen ? "HOME_SELECTED" : "HOME"}
                                            textTop="0"
                                            sz={Platform.OS !== "web" ? 10 : 25}
                                            isGradient={showFacilitatorsScreen ? true : false}
                                        />
                                )}
                            </SafeAreaView>
                        );
                    }}
                    keyExtractor={(item) => `${item}`}
                />
            </View>
            {/* Views to show depending on Flatlist Selection */}
            <SafeAreaView style={[styles.mainSubStyle]}>
                {showMyRegistrationsScreen ? (
                    <ScrollView>
                       <MyRegistrations 
                            usersRegistrations={usersRegistrations}
                            allEvents={allEvents}
                            setRefresh={setRefresh}
                       />
                    </ScrollView>
                ) : (
                    <></>
                )}

                {showFacilitatorsScreen ? (
                    <ScrollView>
                        
                    </ScrollView>
                ) : (
                    <></>
                )}
            </SafeAreaView>
        </SafeAreaView>
    );
};

const makeStyles = (colors, width) =>
    StyleSheet.create({
        main: {
            flex: 1,
            marginHorizontal: 10,
            marginTop: "1%",
        },
        mainSubStyle: {
            flex: 1,
            backgroundColor: colors.background,
        },
        myLeaves: {
            flex: 1,
            backgroundColor: "transparent",
        },
        row: {
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
        },
        circle: {
            width: 70,
            height: 70,
            borderRadius: 35,
            borderWidth: 4,
            borderColor: colors.buttons_primary,
            backgroundColor: colors.background,
            alignItems: "center",
            justifyContent: "center",
            marginTop: 50,
            marginBottom: 10,
        },
    });

export default HomePageEvents;
